/* eslint-disable @typescript-eslint/no-explicit-any */
import type { User } from "@/api/login";
import { Commit, Module, Dispatch } from "vuex";
import { RootState } from "@/store";
import {
  updateUserInfo,
  getUserPersonalInfo,
  quitDiscourse,
  quitSso,
  submitCertification,
} from "@/api/userCenter";
import { showLoading, hideLoading } from "@/components/loading";
import { resetChatwootSession } from "@/utils/chatwoot";

const IS_ELECTRON = process.env.IS_ELECTRON === "true";

function hasElectronRuntime(): boolean {
  return (
    typeof window !== "undefined" &&
    !!(window as unknown as { electronAPI?: unknown }).electronAPI
  );
}

type RendererSentry = {
  setUser?: (user: { id?: string; username?: string } | null) => void;
  setTag?: (key: string, value: string) => void;
};

function getRendererSentry(): RendererSentry | null {
  const anyGlobal = globalThis as unknown as { Sentry?: unknown };
  if (anyGlobal.Sentry && typeof anyGlobal.Sentry === "object") {
    return anyGlobal.Sentry as RendererSentry;
  }
  return null;
}

export interface State {
  userInfo: User;
  isLogin: "0" | "1";
  updateLoading: boolean;
}
const getInitialUserInfo = (): User => {
  const storedUserInfo = localStorage.getItem("userInfo");
  if (storedUserInfo) {
    return JSON.parse(storedUserInfo);
  }
  return {
    id: 0,
    userName: "",
    isCertificated: 0,
    payType: 5,
    userImage: "",
    chainAddress: "",
    credential: "",
    openId: "",
    isRegister: null,
    bindStatus: null,
    firmVerify: 0,
    firmName: "",
    userNo: "",
    description: "",
  };
};
const getInitialIsLogin = (): "0" | "1" => {
  const storedIsLogin = localStorage.getItem("isLogin");
  if (storedIsLogin) {
    return "1";
  }
  return "0";
};
const store: Module<State, RootState> = {
  namespaced: true, // 为每个模块添加一个前缀名，保证模块命明不冲突
  state: () => {
    return {
      userInfo: getInitialUserInfo(),
      updateLoading: false,
      isLogin: getInitialIsLogin(),
    };
  },
  mutations: {
    setUserInfo(state, payload) {
      console.log("setUserInfo");
      const userInfoToStore = { ...payload };

      // 检查credential是否存在并且长度为127位
      if (userInfoToStore.credential) {
        // 计算需要补充的0的数量
        const zerosNeeded = 128 - userInfoToStore.credential.length;

        // 如果长度不足128位，则在前面补齐0
        if (zerosNeeded > 0) {
          userInfoToStore.credential =
            "0".repeat(zerosNeeded) + userInfoToStore.credential;
        }
      }
      state.userInfo = userInfoToStore;
      payload &&
        localStorage.setItem("userInfo", JSON.stringify(userInfoToStore));
      payload && localStorage.setItem("isLogin", "1");
      !payload && localStorage.removeItem("isLogin");

      const sentry = getRendererSentry();
      if (payload) {
        const userId = userInfoToStore?.id;
        const userNo = userInfoToStore?.userNo;
        const openId = userInfoToStore?.openId;
        // 主进程任务文件索引 key：优先 openId，缺失时使用 userNo，避免主进程 user.openId 为空。
        const openIdForMain = openId || userNo || "";

        sentry?.setUser?.({
          id: userId !== undefined && userId !== null ? String(userId) : "",
          username: userNo ? String(userNo) : "",
        });

        if (userId !== undefined && userId !== null) {
          sentry?.setTag?.("userId", String(userId));
        }
        if (userNo) {
          sentry?.setTag?.("userNo", String(userNo));
        }
        if (openIdForMain) {
          sentry?.setTag?.("openId", String(openIdForMain));
        }

        if (hasElectronRuntime()) {
          window.electronAPI.setUser({
            userId:
              userId !== undefined && userId !== null ? String(userId) : "",
            userNo: userNo ? String(userNo) : "",
            openId: String(openIdForMain),
          });
        }
      } else {
        sentry?.setUser?.(null);
        if (hasElectronRuntime()) {
          window.electronAPI.setUser(null);
        }
      }
    },
    setIsLogin(state, payload) {
      state.isLogin = payload;
      localStorage.setItem("isLogin", payload);
    },
    setUpdateLoading(state, payload) {
      state.updateLoading = payload;
    },
  },
  actions: {
    async submitEnterpriseCertification(
      { commit: _commit },
      data: Parameters<typeof submitCertification>[0]
    ) {
      // 调用认证接口
      const response = await submitCertification(data);
      // // 更新认证状态
      getUserPersonalInfo();
      return response;
    },
    getUserInfo({ commit }: { commit: Commit }) {
      const userInfo = localStorage.getItem("userInfo");
      if (userInfo) {
        const userInfoObj = JSON.parse(userInfo);
        commit("setUserInfo", userInfoObj);
        commit("setIsLogin", "1");
        if (window._paq) {
          window._paq.push([
            "setUserId",
            userInfoObj.userName + "（" + userInfoObj.id + "）",
          ]);
          window._paq.push(["trackPageView"]);
        }
      }
    },
    async logout({ commit }: { commit: Commit }) {
      const hadToken = !!localStorage.getItem("token");

      try {
        localStorage.removeItem("userInfo");
        localStorage.removeItem("isLogin");
        localStorage.removeItem("token");
        localStorage.removeItem("casdoorToken");
        localStorage.removeItem("refreshToken");
      } catch {
        // ignore
      }

      try {
        sessionStorage.clear();
      } catch {
        // ignore
      }

      try {
        const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
        document.cookie = `token=; expires=${expired}; path=/; samesite=strict`;
        document.cookie = `token=; expires=${expired}; path=/; secure; samesite=strict`;
        document.cookie = `token=; expires=${expired}; path=/`;
      } catch {
        // ignore
      }

      commit("setIsLogin", "0");
      commit("setUserInfo", null);
      resetChatwootSession();

      if (hadToken) {
        // 网页环境才需要退出 discourse，客户端环境不需要
        if (!IS_ELECTRON) {
          // 静默处理退出 discourse 的错误，避免阻止用户退出登录
          try {
            await quitDiscourse();
          } catch (error) {
            // 退出 discourse 失败不应该阻止用户退出登录，静默处理错误
          }
        }
        try {
          if (window._paq) {
            window._paq.push(["resetUserId"]);
          }
          showLoading();

          // NOTE: 直连 SSO 的 front-channel logout（Electron hidden window / Web popup）目前统一停用，
          // 还原为调用业务后端 quitSso(sessionId) 方式。
          //
          // const webSsoLogout = async () => { ... };
          // if (IS_ELECTRON) {
          //   await window.electronAPI.ssoLogout();
          // } else {
          //   await webSsoLogout();
          // }

          const getCasdoorSessionId = (): string => {
            try {
              const cookies = document.cookie.split(";");
              for (const cookie of cookies) {
                const [name, value] = cookie.trim().split("=");
                if (name === "casdoor_session_id") {
                  return value || "";
                }
              }
              return "";
            } catch {
              return "";
            }
          };

          const sessionId = getCasdoorSessionId();
          if (!sessionId) {
            console.warn(
              "[logout] casdoor_session_id is missing; continue quitSso with empty sessionId"
            );
          }
          await quitSso(sessionId);
        } catch (error) {
          console.log(error);
        } finally {
          hideLoading();
        }
      }
    },
    async updateUserInfo(
      {
        commit,
        state,
        dispatch,
      }: { commit: Commit; state: State; dispatch: Dispatch },
      payload: {
        params: {
          name?: string;
          image?: File;
          password?: string;
          updateDescription?: boolean;
          description?: string;
        };
        onSuccess?: () => void;
        onError?: (msg: string) => void;
      }
    ) {
      const { params, onSuccess, onError } = payload;
      commit("setUpdateLoading", true);
      try {
        await updateUserInfo({
          ...params,
          id: Number(state.userInfo.id),
        });
        commit("setUpdateLoading", false);
        dispatch("getUserPersonalInfo");
        onSuccess && onSuccess();
      } catch (error: any) {
        onError && onError(error.resultDesc);
        commit("setUpdateLoading", false);
      }
    },
    async getUserPersonalInfo({ commit }: { commit: Commit }) {
      try {
        const res = await getUserPersonalInfo();
        commit("setUserInfo", res);
        commit("setIsLogin", "1");
        return;
      } catch (error) {
        log.error(error, "error");
      }
    },
  },
};

export default store;
